import styled, { useTheme } from "styled-components";
import Modal from "../../components/Modal/Modal";
import * as AiIcons from "react-icons/ai";
import StyledForm from "../../components/Form/Form";
import TextField from "../../components/FormFields/TextField";
import { ModalContainer } from "../../components/Modal/Modal";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { OrderModel, TransactionTypeOptions } from "./OrderInterfaces";
import { TransactionType } from "./OrderInterfaces";
import { FormikProvider, useFormik } from "formik";
import Icon from "../../components/Icon/Icon";
import assetsConfig from "../../assetsConfig";
import PrimaryLogo from "../../components/PrimaryLogo/PrimaryLogo";
import { getLocalTime } from "../../utils/timeUtilities";
import { copy } from "../../utils/copy";
import { getSubmissionLabels } from "../../utils/getLabels";
import { get } from "sortablejs";
import Currency from "../../components/Currency/Currency";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

const StyledModal = styled(Modal)`
	overflow-y: auto;
	overflow-x: hidden;

	${ModalContainer} {
		width: 800px;
	}

	.inner-container {
		gap: 1.5rem;
	}

	.section {
		padding: 1rem;
	}

	hr {
		border-top: solid 2px ${(props) => props.theme.colorBorder};
	}

	ul {
		padding-bottom: 0;
		padding-top: 0;
	}
`;

const OrderDetail = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	align-items: center;
	gap: 0.5rem;
	&,
	* {
		font-size: 22px;
	}

	.label {
		color: ${(props) => props.theme.colorCopyDark};
	}
`;

const SectionHeader = styled.h1`
	font-family: ${({ theme }) =>
		`${theme.fontBrand.fontFamily}${
			theme.fontBrand.fontFallback ? `, ${theme.fontBrand.fontFallback}` : ""
		}`};
	font-weight: 600;
	margin-bottom: 1rem;
`;

const IndentedDiv = styled.div`
	margin-left: 20px;
`;

const SubmissionLightbox = (props: SubmissionLightboxProps) => {
	const { settings } = useSelector((state: RootState) => state.settings);
	const history = useHistory();
	const theme = useTheme();
	const [offlinePaymentInformation, setOfflinePaymentInformation] =
		useState<string>();

	useEffect(() => {
		copy("offlinePaymentInformation")
			.then((text) => {
				setOfflinePaymentInformation(text);
			})
			.catch();
	}, []);

	const formikProps = useFormik({
		initialValues: props.order,
		onSubmit: () => {},
	});
	const [labels, setLabels] = useState<Record<string, string> | null>(null);
	let countryIso = settings.countryIso;

	useEffect(() => {
		getSubmissionLabels().then(setLabels);
	}, []);

	return labels ? (
		<StyledModal show={props.show && labels !== null}>
			<ModalContainer>
				<FormikProvider value={formikProps}>
					<Icon
						className="m-[1.5rem] ml-auto"
						icon="close"
						color={theme.colorCopyDarkDark}
						width="30px"
						height="30px"
						onClick={() => history.push("/entries/orders")}
					/>
					<div className="inner-container">
						<div className="section">
							<PrimaryLogo
								className="mb-[2rem]"
								color={theme.colorLogoDarkDark}
								width="250px"
							/>
							<h2 className="font-semibold">Thank You For Your Submission</h2>
							{(props.order.transactionType === TransactionType.Check ||
								props.order.transactionType === TransactionType.Bank) && (
								<p>
									You're almost done! Please follow the instructions below to
									complete your entry
								</p>
							)}
							{(props.order.transactionType === TransactionType.Credit ||
								props.order.transactionType === TransactionType.Submission) && (
								<p>Submission received and payment completed successfully.</p>
							)}
						</div>

						<hr />

						<div className="section flex flex-col gap-[1rem]">
							<SectionHeader>Order Details</SectionHeader>
							<OrderDetail>
								<span className="label">Company Name</span>
								<span>{props.order.companyName || "company Name"}</span>
							</OrderDetail>
							<OrderDetail>
								<span className="label">Order ID</span>
								<span>#{props.order.id}</span>
							</OrderDetail>
							<OrderDetail>
								<span className="label">Order Total</span>
								<span>
									<Currency
										amount={props.order.orderTotal}
										countryIso={countryIso}
									/>
								</span>
							</OrderDetail>
							<OrderDetail>
								<span className="label">Order Date</span>
								<span>
									{props.order.createdDate &&
										getLocalTime(props.order.createdDate!, false)}
								</span>
							</OrderDetail>
							{(props.order.transactionType === TransactionType.Check ||
								props.order.transactionType === TransactionType.Bank) &&
								props.order.purchaseOrder && (
									<OrderDetail>
										<span className="label">Purchase Order</span>
										<span>{props.order.purchaseOrder}</span>
									</OrderDetail>
								)}
							<OrderDetail>
								<span className="label">Payment Method</span>
								<span>
									{props.order.transactionType === 4
										? TransactionTypeOptions[4].label
										: `${TransactionType[props.order.transactionType]} Payment`}
								</span>
							</OrderDetail>
							<OrderDetail>
								<span className="label">Proforma Invoice</span>
								<a
									className="flex items-center text-colorActivation"
									onClick={() =>
										window.open(`${props.order.invoicePDF}`, "_blank")
									}
								>
									<Icon
										className="mr-[.5rem]"
										icon="download"
										color={theme.colorActivation}
										width="15px"
										height="15px"
									/>

									{props.order.invoicePDF ? (
										"Download"
									) : (
										<p className="text-colorDanger">PDF not available</p>
									)}
								</a>
							</OrderDetail>
						</div>

						{(props.order.transactionType === TransactionType.Check ||
							props.order.transactionType === TransactionType.Bank) && (
							<>
								<hr />
								<div className="section">
									<SectionHeader className="!mb-[2rem]">
										{TransactionType[props.order.transactionType]} Information
									</SectionHeader>

									{props.order.transactionType == TransactionType.Check && (
										<div className="flex flex-col gap-[1rem]">
											<p>
												Take note of your Order ID: <b>"{props.order.id}"</b>{" "}
												and your Billing Name:{" "}
												<b>"{props.order.companyName}"</b> and include it in the
												note section of your check.
											</p>
											<p>
												Make your check payable to:
												<br />
												<IndentedDiv>{labels.checkPayable}</IndentedDiv>
											</p>
											<p>
												Mail your check to: <br />
												<IndentedDiv>
													{labels.checkPayable} <br />
													{labels.checkAddress1 && (
														<>
															{labels.checkAddress1} <br />
														</>
													)}
													{labels.checkAddress2 && (
														<>
															{labels.checkAddress2} <br />
														</>
													)}
													{labels.checkCity}, {labels.checkState}{" "}
													{labels.checkZipCode} <br />
													{labels.checkCountry}
												</IndentedDiv>
											</p>
											<p>
												Please Remember: <br />
												<IndentedDiv>
													<ul>
														<li>
															Reference your Order ID and your Billing Name on
															your check in the notes section.
														</li>
														{labels.checkPaymentNote && (
															<li>{labels.checkPaymentNote}</li>
														)}
														<li>
															Include a copy of the invoice in the envelope.
														</li>
													</ul>
												</IndentedDiv>
											</p>
										</div>
									)}

									{props.order.transactionType == TransactionType.Bank && (
										<div className="flex flex-col gap-[1rem]">
											<p>
												Take note of your Order ID: <b>"{props.order.id}"</b>{" "}
												and your Billing Name:{" "}
												<b>"{props.order.companyName}"</b> and include it in the
												note section of your bank transfer.
											</p>
											<p>
												Name on Account: <br />
												<IndentedDiv>{labels.nameOnBankAccount}</IndentedDiv>
											</p>
											<p>
												Bank Information: <br />
												<IndentedDiv>
													{labels.nameOnBankAccount} <br />
													{labels.bankAddress1 && (
														<>
															{labels.bankAddress1} <br />
														</>
													)}
													{labels.bankAddress2 && (
														<>
															{labels.bankAddress1} <br />
														</>
													)}
													{labels.bankCity}, {labels.bankState}{" "}
													{labels.bankZipCode} <br />
													{labels.bankCountry}
													<br />
													{labels.bankAccountNumber && (
														<>
															Account #: {labels.bankAccountNumber} <br />
														</>
													)}
													{labels.bankRoutingNumber && (
														<>
															Routing #: {labels.bankRoutingNumber} <br />
														</>
													)}
													{labels.bankSwiftCode && (
														<>
															Swift Code: {labels.bankSwiftCode} <br />
														</>
													)}
												</IndentedDiv>
											</p>
											<p>
												Please Remember: <br />
												<IndentedDiv>
													<ul>
														<li>
															Reference your Order ID and your Company Name on
															your Bank Transfer in the notes section.
														</li>
														{labels.bankTransferNote && (
															<li>{labels.bankTransferNote}</li>
														)}
													</ul>
												</IndentedDiv>
											</p>

											<TextField
												name="bankName"
												placeholder="Bank Name"
												value={props.order.bankName}
												nonFormik={true}
												readOnly
											/>

											<TextField
												name="bankCity"
												placeholder="Bank City"
												value={props.order.bankCity}
												nonFormik={true}
												readOnly
											/>
										</div>
									)}
								</div>
							</>
						)}

						{props.order.transactionType === TransactionType.Offline && (
							<>
								<hr />
								<div className="section">
									<SectionHeader className="!mb-[2rem]">
										Payment Instructions
									</SectionHeader>

									<div className="flex flex-col gap-[1rem]">
										{offlinePaymentInformation && (
											<p
												className="mt-[1rem]"
												dangerouslySetInnerHTML={{
													__html: offlinePaymentInformation,
												}}
											/>
										)}
									</div>
								</div>
							</>
						)}

						{settings.featureFlags["Invoice Notes"] && props.order.notes && (
							<>
								<hr />
								<div className="section">
									<SectionHeader className="!mb-[2rem]">
										Invoice Notes
									</SectionHeader>

									<TextField
										name="notes"
										// placeholder="Invoice Notes"
										value={props.order.notes}
										component="textarea"
										height="200px"
										nonFormik={true}
										readOnly
									/>
								</div>
							</>
						)}
					</div>
				</FormikProvider>
			</ModalContainer>
		</StyledModal>
	) : null;
};

export default SubmissionLightbox;

interface SubmissionLightboxProps {
	show: boolean;
	order: OrderModel;
}

// type SubmissionLightboxProps = Lightbox & order;

// interface Lightbox {
//   show: boolean;
//   //   hideModal(): void;
// }
